class LocalizationDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawerClass = "wt-localization-drawer";
    this.drawer = this;
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "wt-localization-drawer-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      ".wt-localization-trigger",
      ".wt-localization-drawer__close",
      `.${this.pageOverlayClass}`,
    ].join(", ");

    this.isOpen = false;
    this.mainTrigger =
      document.querySelector(".wt-header__localization-trigger") || null;
    this.lastOpenerEl = null;

    this.focusableSelector =
      'button:not([disabled]), a[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.init();
    this.inert = true;
    this.setAttribute("aria-hidden", "true");

    // Theme editor: re-bind triggers when the section reloads.
    if (window.Shopify?.designMode) {
      this._onSectionLoad = () => {
        document.querySelectorAll(this.triggerQuery).forEach((trigger) => {
          if (trigger._wtLocBound) return;
          trigger._wtLocBound = true;
          trigger.addEventListener("click", (e) => {
            e.preventDefault();
            this.storeOpenerElement(e.currentTarget);
            this.toggleDrawerClasses();
          });
        });
      };
      document.addEventListener("shopify:section:load", this._onSectionLoad);
    }
  }

  disconnectedCallback() {
    if (this._onSectionLoad) {
      document.removeEventListener("shopify:section:load", this._onSectionLoad);
      this._onSectionLoad = null;
    }
    if (this._onKeydown) {
      document.removeEventListener("keydown", this._onKeydown);
      this._onKeydown = null;
    }
  }

  storeOpenerElement(triggerEl) {
    const candidate = triggerEl || document.activeElement;
    this.lastOpenerEl =
      candidate && candidate instanceof HTMLElement ? candidate : null;
  }

  restoreFocusToOpener() {
    const el = this.lastOpenerEl;
    if (!el || !document.contains(el) || el.hasAttribute("disabled")) {
      return false;
    }
    try {
      el.focus();
      this.lastOpenerEl = null;
      return true;
    } catch {
      return false;
    }
  }

  getAllowedFocusableElements() {
    return Array.from(
      this.querySelectorAll(this.focusableSelector),
    ).filter((el) => el.offsetParent !== null);
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
    setTimeout(() => document.body.classList.remove("no-focus-visible"), 200);
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.inert = true;
      this.setAttribute("aria-hidden", "true");
      requestAnimationFrame(() => {
        const restored = this.restoreFocusToOpener();
        if (!restored && this.mainTrigger) this.mainTrigger.focus();
      });
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.inert = false;
      this.setAttribute("aria-hidden", "false");
      const closeBtn = this.querySelector(".wt-localization-drawer__close");
      if (closeBtn) closeBtn.focus();
    }
    this.temporaryHideFocusVisible();
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    // Close on Escape (bound globally so it works even when focus has
    // left the modal) + focus trap while focus is inside the modal.
    this._onKeydown = (e) => {
      const isEsc =
        e.key === "Escape" || e.keyCode === 27 || e.code === "Escape";
      if (isEsc) {
        if (!this.isOpen) return;
        e.preventDefault();
        this.toggleDrawerClasses();
        return;
      }
      const isTab = e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      if (!isTab || !this.isOpen) return;
      if (!this.contains(document.activeElement)) return;
      const focusables = this.getAllowedFocusableElements();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!focusables.includes(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", this._onKeydown);

    // Open/close triggers
    document.querySelectorAll(this.triggerQuery).forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.storeOpenerElement(e.currentTarget);
        // Trigger lives inside the mobile nav drawer → close the nav
        // drawer first, otherwise it stays open behind the modal and
        // covers the page after the modal is dismissed.
        if (trigger.closest("#wt-drawer-nav")) {
          const navClose = document.querySelector(
            "#wt-drawer-nav .wt-drawer__close",
          );
          if (navClose) navClose.click();
        }
        this.toggleDrawerClasses();
      });
    });

    // Country cards -> navigate to the target storefront domain
    this.querySelectorAll(".wt-locale-item__btn").forEach((card) => {
      card.addEventListener("click", () => {
        // Clicking the currently-active country just closes the modal.
        if (card.classList.contains("wt-locale-item__btn--current")) {
          this.toggleDrawerClasses();
          return;
        }
        const targetUrl = card.dataset.url;
        if (targetUrl) {
          window.location.href = targetUrl;
        }
      });
    });

    // Language pills -> submit language form
    const languageForm = document.querySelector("#DrawerLanguageForm");
    if (languageForm) {
      languageForm
        .querySelectorAll('input[name="language_code"]')
        .forEach((radio) => {
          radio.addEventListener("change", () => languageForm.submit());
        });
    }
  }
}

customElements.define("localization-drawer", LocalizationDrawer);
